import {
  IntelPlans,
  UserPlans,
  type InvitedUser,
  type ProjectUser,
  type User,
  type UserForm,
} from '@pn/core/domain/user';
import { nullToUndefined } from '@pn/core/utils/logic';
import { apiUserEnterpriseMapper } from '@pn/services/api/enterprise/apiEnterpriseMapper';
import { isNil } from 'lodash-es';
import {
  isApiEnterpriseUser,
  isApiIncompleteUser,
  isApiUser,
  type ApiEnterpriseInvitedUser,
  type ApiEnterpriseUser,
  type ApiIncompleteUser,
  type ApiProjectUser,
  type ApiTargetUser,
  type ApiUser,
} from './types';

export const apiUserMapper = {
  toDomainUser: (
    apiUser: ApiUser | ApiIncompleteUser | ApiEnterpriseUser
  ): User => {
    if (isApiUser(apiUser)) {
      return {
        id: apiUser.id,
        isInvited: false,
        email: apiUser.email,
        name: {
          firstName: apiUser.first_name ?? '',
          lastName: apiUser.last_name ?? '',
        },
        company: !isNil(apiUser.enterprise)
          ? apiUser.enterprise.name
          : (apiUser.company ?? ''),
        title: apiUser.title ?? '',
        userPlan: apiUser.plan?.maps ?? UserPlans.Free, // TODO: apiUser.plan.maps
        intelPlan: apiUser.plan?.intel ?? IntelPlans.Starter, // TODO: apiUser.plan.intel
        enterprise: !isNil(apiUser.enterprise)
          ? apiUserEnterpriseMapper.toDomainUserEnterprise(apiUser.enterprise)
          : undefined,
        stackCompanyId: nullToUndefined(apiUser.enterprise?.stack_company_id),
        authenticationType: nullToUndefined(apiUser.connection),
        hasGptAccess: apiUser.has_gpt_access ?? false,
        gptUsage: {
          freeCreditsRemaining: apiUser.gpt_usage.free_credits_remaining ?? 0,
          paidCreditsBalance: apiUser.gpt_usage.paid_credits_balance ?? 0,
          freeCreditsResetAt:
            apiUser.gpt_usage.free_credits_reset_at ??
            new Date(0).toISOString(),
        },
        createdAt: apiUser.created_at,
        stripeAccount: nullToUndefined(apiUser.stripe_account),
        isAdmin: apiUser.is_admin,
      };
    } else if (isApiIncompleteUser(apiUser) || isApiEnterpriseUser(apiUser)) {
      const enterprise =
        'enterprise' in apiUser ? apiUser.enterprise : undefined;

      return {
        id: apiUser.id,
        isInvited: !isNil(enterprise),
        email: apiUser.email,
        name: {
          firstName: 'first_name' in apiUser ? apiUser.first_name : '',
          lastName: 'last_name' in apiUser ? apiUser.last_name : '',
        },
        company: enterprise?.name ?? '',
        title: '',
        userPlan: apiUser.user_plan ?? UserPlans.Free,
        enterprise: !isNil(enterprise)
          ? apiUserEnterpriseMapper.toDomainUserEnterprise(enterprise)
          : undefined,
        stackCompanyId: nullToUndefined(enterprise?.stack_company_id),
        authenticationType:
          'connection' in apiUser
            ? nullToUndefined(apiUser.connection)
            : undefined,
        intelPlan: IntelPlans.Starter,
        hasGptAccess: false,
        gptUsage: {
          freeCreditsRemaining: 0,
          paidCreditsBalance: 0,
          freeCreditsResetAt: new Date(0).toISOString(),
        },
        stripeAccount: undefined,
        isAdmin: false,
      };
    } else {
      throw new Error('Invalid API user');
    }
  },
  toTargetUser: (domainUser: User): ApiTargetUser => {
    /**
     * UserForm         -> User -> ApiUser
     * User['userPlan'] -> User -> ApiUser
     */
    return {
      email: domainUser.email,
      first_name: domainUser.name.firstName,
      last_name: domainUser.name.lastName,
      company: domainUser.company,
      title: domainUser.title,
      user_plan: domainUser.userPlan,
      enterprise_id: domainUser?.enterprise?.id,
      intel_plan: domainUser.intelPlan,
    };
  },
  toTargetUserProfile: (userForm: UserForm): Partial<ApiUser> => {
    return {
      email: userForm.email,
      first_name: userForm.firstName,
      last_name: userForm.lastName,
      company: userForm.company,
      title: userForm.title,
    };
  },
};

export const apiInvitedUserMapper = {
  toDomainInvitedUser: (
    apiInvitedUser: ApiEnterpriseInvitedUser
  ): InvitedUser => {
    return {
      id: apiInvitedUser.id,
      email: apiInvitedUser.email,
      userPlan: apiInvitedUser.user_plan,
      enterprise: undefined,
      createdAt: apiInvitedUser.created_at,
      intelPlan: IntelPlans.Starter,
    };
  },
  toTargetInvitedUser: (
    domainInvitedUser: InvitedUser
  ): Partial<ApiEnterpriseInvitedUser> => {
    return {
      email: domainInvitedUser.email,
      user_plan: domainInvitedUser.userPlan,
    };
  },
};

export const apiProjectUserMapper = {
  toDomainProjectUser: (apiProjectUser: ApiProjectUser): ProjectUser => {
    return {
      id: apiProjectUser.id,
      email: apiProjectUser.email,
      name: {
        firstName: apiProjectUser.first_name ?? '',
        lastName: apiProjectUser.last_name ?? '',
      },
    };
  },
};
